import { useContext, useMemo, useCallback } from "react";
import { AnalyticsContext } from "@swiggy-private/checkout-core";
import { IInstamartGiftingWidgetPopUpDetails, GiftingCartApiResponse } from "@swiggy-private/cart-types";
import { SCREEN_NAME } from "@cart-client/analytics/constants";
import { ANALYTICS_EVENT_NAMES } from "./constants";

interface GetAnalyticsProps {
    popupDetails?: IInstamartGiftingWidgetPopUpDetails;
    reviewedCart?: GiftingCartApiResponse | null;
}

export const useGetGiftBagAnalytics = ({ popupDetails, reviewedCart }: GetAnalyticsProps) => {
    const { impressionEvent, clickEvent } = useContext(AnalyticsContext);

    const { primaryCTAText, secondaryCTAText } = useMemo(() => {
        return {
            primaryCTAText: popupDetails?.ctaButtons?.filter(cta => cta.giftBagPackagingEnabled)?.[0]?.text,
            secondaryCTAText: popupDetails?.ctaButtons?.filter(cta => !cta.giftBagPackagingEnabled)?.[0]?.text,
        };
    }, [popupDetails?.ctaButtons]);

    const payload = useMemo(() => {
        return JSON.stringify({
            restaurantId: reviewedCart?.restaurant_details?.id,
            originalCartId: reviewedCart?.originalCartId,
            cartId: reviewedCart?.cartId,
            description: popupDetails?.description,
        });
    }, [
        popupDetails?.description,
        reviewedCart?.cartId,
        reviewedCart?.originalCartId,
        reviewedCart?.restaurant_details?.id,
    ]);

    const impressionHalfCard = useCallback(() => {
        return impressionEvent({
            category: SCREEN_NAME.GIFTING_CHECKOUT,
            action: ANALYTICS_EVENT_NAMES.IMPRESSION.GIFTBAG_HALFCARD,
            extraParams: payload,
        });
    }, [impressionEvent, payload]);

    const impressionPrimaryButton = useCallback(() => {
        return impressionEvent({
            category: SCREEN_NAME.GIFTING_CHECKOUT,
            action: ANALYTICS_EVENT_NAMES.IMPRESSION.GIFTBAG_PRIMARY_BUTTON,
            extraParams: payload,
            context: JSON.stringify(primaryCTAText),
        });
    }, [impressionEvent, payload, primaryCTAText]);

    const impressionSecondaryButton = useCallback(() => {
        return impressionEvent({
            category: SCREEN_NAME.GIFTING_CHECKOUT,
            action: ANALYTICS_EVENT_NAMES.IMPRESSION.GIFTBAG_SECONDARY_BUTTON,
            extraParams: payload,
            context: JSON.stringify(secondaryCTAText),
        });
    }, [impressionEvent, payload, secondaryCTAText]);

    const clickCrossCTA = useCallback(() => {
        return clickEvent({
            category: SCREEN_NAME.GIFTING_CHECKOUT,
            action: ANALYTICS_EVENT_NAMES.CLICK.GIFTBAG_CROSS_CTA,
            extraParams: payload,
        });
    }, [clickEvent, payload]);

    const clickPrimaryButton = useCallback(() => {
        return clickEvent({
            category: SCREEN_NAME.GIFTING_CHECKOUT,
            action: ANALYTICS_EVENT_NAMES.CLICK.GIFTBAG_PRIMARY_BUTTON,
            extraParams: payload,
            context: JSON.stringify(primaryCTAText),
        });
    }, [clickEvent, payload, primaryCTAText]);

    const clickSecondaryButton = useCallback(() => {
        return clickEvent({
            category: SCREEN_NAME.GIFTING_CHECKOUT,
            action: ANALYTICS_EVENT_NAMES.CLICK.GIFTBAG_SECONDARY_BUTTON,
            extraParams: payload,
            context: JSON.stringify(secondaryCTAText),
        });
    }, [clickEvent, payload, secondaryCTAText]);

    return {
        impressionHalfCard,
        impressionPrimaryButton,
        impressionSecondaryButton,
        clickCrossCTA,
        clickPrimaryButton,
        clickSecondaryButton,
    };
};
